import React, { useEffect } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader } from "lucide-react";
import { useListCustomers } from "@/lib/api/customer";
import { useListMarketZones, useListLocations } from "@/lib/api/generalContract";
import { useListTraders } from "@/lib/api/trader";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useFormField } from "@/components/ui/form";
import { Combobox } from "@/components/ui/combobox";
import { CommodityOptions } from "@/lib/api/commodity";
import { CashSaleStatus, PrStatus, RiskCashSale } from "@/lib/api/risk/cashSales/general";
import { AcceptedCommodity } from "@/lib/api/risk/profiles";
import { PriceCashSaleSchema } from "@/lib/api/risk/manualEntries";
import { SelectOptionItem } from "@/lib/models/ui";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { formulateOptionMonthsWithOlder } from "@/lib/api/common";
import { ContractTypeOptions as OtcContractTypeOptions } from "@/lib/api/otc";
import { generateContractTypesWith as generateGeneralContractTypesWith } from "@/lib/api/generalContract";
import { cn } from "@/lib/utils";
import { CommonDestinationTimezone } from "@/lib/utils";
import { safeParseDecimal } from "@/lib/api/common";
import { FormInput, FormCombobox, FormTextarea } from "./FormComponents";
import { type MarketZone } from "@/lib/api/generalContract";

export type CreateFormData = z.infer<typeof PriceCashSaleSchema>;
export type PriceCashSalesFormData = CreateFormData;

interface PriceCashSalesFormPropsBase {
    onCancel?: () => void;
    showCancelButton?: boolean;
    isSubmitting?: boolean;
    marketZones?: MarketZone[];
}

interface CreateFormProps extends PriceCashSalesFormPropsBase {
    mode: "create";
    initialData?: RiskCashSale | null;
    onSubmit: (data: CreateFormData) => Promise<void>;
}

type CashSalesFormProps = CreateFormProps;

export function PriceCashSalesForm(props: CashSalesFormProps) {
    const { mode, initialData, onSubmit, onCancel, showCancelButton = true, isSubmitting = false, marketZones = [] } = props;

    // Use the appropriate schema based on mode
    const formSchema = PriceCashSaleSchema;

    // Create market zone options
    const marketZoneOptions: SelectOptionItem<string>[] = marketZones.map((zone: any) => ({
        value: zone.id,
        label: zone.dtn_market_zone,
    }));

    // Create form with proper typing
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: "onChange",
        defaultValues: {
            base_id: initialData?.id || "",
            priced_quantity: initialData?.quantity?.toString() || "",
            futures: initialData?.futures?.toString() || "",
            basis: initialData?.basis?.toString() || "",
            option_month: initialData?.option_month?.toString() || undefined,
            market_zone_id: initialData?.market_zone_id || "",
            start_date: initialData?.start_date ? moment.tz(initialData.start_date, CommonDestinationTimezone).format("YYYY-MM-DD") : "",
            end_date: initialData?.end_date ? moment.tz(initialData.end_date, CommonDestinationTimezone).format("YYYY-MM-DD") : "",
        },
    });

    // Reset form when initialData changes
    useEffect(() => {
        if (initialData) {
            form.reset({
                base_id: initialData.id || "",
                priced_quantity: initialData.quantity?.toString() || undefined,
                futures: initialData.futures?.toString() || undefined,
                basis: initialData.basis?.toString() || undefined,
                option_month: initialData?.option_month?.toString() || undefined,
                market_zone_id: initialData.market_zone_id || undefined,
                start_date: initialData.start_date ? moment.tz(initialData.start_date, CommonDestinationTimezone).format("YYYY-MM-DD") : undefined,
                end_date: initialData.end_date ? moment.tz(initialData.end_date, CommonDestinationTimezone).format("YYYY-MM-DD") : undefined,
            });
        }
    }, [initialData, form, mode]);

    const handleSubmit = form.handleSubmit((data) => {
        return onSubmit(data as CreateFormData);
    });

    const futuresValue = form.watch('futures');
    const basisValue = form.watch('basis');

    return (
        <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
                <>
                    {/* Quantities and Pricing - only for create mode */}
                    <div>
                        <h3 className="text-lg font-medium mb-4">Quantities and Pricing</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <FormField
                                control={form.control}
                                name="priced_quantity"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Quantity *</FormLabel>
                                        <FormControl>
                                            <FormInput
                                                {...field}
                                                value={field.value || ""}
                                                placeholder="Enter quantity"
                                                type="number"
                                                step="0.01"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="futures"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Futures</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={field.value || ""}
                                                placeholder="Enter futures"
                                                type="number"
                                                step="0.01"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="basis"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Basis</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={field.value || ""}
                                                placeholder="Enter basis"
                                                type="number"
                                                step="0.01"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="market_zone_id"
                                render={({ field }) => (
                                    <FormItem className="grid">
                                        <FormLabel>Market Zone</FormLabel>
                                        <FormControl>
                                            <FormCombobox
                                                dwimOnChange
                                                isModal
                                                virtualizeList
                                                options={marketZoneOptions}
                                                value={field.value || ""}
                                                onValueChange={field.onChange}
                                                placeholder="Select market zone..."
                                                className="w-full"
                                                popoverClassName="md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="option_month"
                                render={({ field }) => (
                                    <FormItem className="grid">
                                        <FormLabel>Option Month</FormLabel>
                                        <FormControl>
                                            <FormCombobox
                                                dwimOnChange
                                                isModal
                                                virtualizeList
                                                options={formulateOptionMonthsWithOlder(field.value)}
                                                value={field.value || ""}
                                                onValueChange={field.onChange}
                                                placeholder="Select month..."
                                                className="w-full"
                                                popoverClassName="w-full min-w-[300px]"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    {/* Date Fields */}
                    <div>
                        <h3 className="text-lg font-medium mb-4">Date Range</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="start_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Start Date</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={field.value || ""}
                                                type="date"
                                                placeholder="YYYY-MM-DD"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="end_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>End Date</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={field.value || ""}
                                                type="date"
                                                placeholder="YYYY-MM-DD"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                </>

                <div className="flex justify-end gap-2 pt-4">
                    {showCancelButton && onCancel && (
                        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                {mode === "create" ? "Creating..." : "Updating..."}
                            </>
                        ) : (
                            mode === "create" ? "Price Cash Sale" : ""
                        )}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
